import { FormEvent, SyntheticEvent, useEffect, useState } from "react";
import differenceInYears from "date-fns/differenceInYears";
import { validate } from "email-validator";
import isEmpty from "lodash/isEmpty";
import { useDispatch, useSelector } from "react-redux";

import putUser from "api/requests/putUser";
import { CANADA_AGE_REQUIREMENT, US_AGE_REQUIREMENT } from "constants/checkout";
import { User } from "custom-types/User";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { USER_LOAD, UserLoadAction } from "redux/reducers/user";
import { getFullUser } from "redux/selectors/user";
import { createGetServerSideProps } from "utils/createGetServerSideProps";
import isCanada from "utils/isCanada";

import Input from "components/botanic/Input";
import DateSelectors from "components/DateSelectors/DateSelectors";
import DeleteAccountButton from "components/UserProfile/EditProfile/DeleteAccountButton";
import EmailVerificationStatus from "components/UserProfile/EditProfile/EmailVerificationStatus";
import UserProfileLayout from "components/UserProfile/UserProfileLayout";

const EditProfile: React.FC = () => {
  const dispatch = useDispatch();
  const countryCode = useDomainCountryCode();

  const [editableUserValues, setEditableUserValues] = useState<User>();
  const [isLoading, setIsLoading] = useState(true);
  const [dob, setDob] = useState({
    day: 0,
    month: 0,
    year: 0,
  });

  const [form, setForm] = useState({
    errorEmailText: "",
    errorUsernameText: "",
    errorsOnSave: false,
    updateSuccess: false,
    validDob: true,
    validEmail: true,
    validPhoneNumber: true,
  });

  const loggedInUser = useSelector(getFullUser);

  useEffect(() => {
    if (loggedInUser.id && loggedInUser.username) {
      setEditableUserValues({
        dateOfBirth: loggedInUser.dateOfBirth,
        email: loggedInUser.email,
        name: loggedInUser.name,
        phoneNumber: loggedInUser.phoneNumber,
        preferredName: loggedInUser.preferredName,
        username: loggedInUser.username,
      });

      const formattedDob =
        loggedInUser.dateOfBirth &&
        new Date(loggedInUser.dateOfBirth.replace(/-/g, "/"));

      if (formattedDob) {
        setDob({
          day: formattedDob.getDate(),
          month: formattedDob.getMonth() + 1,
          year: formattedDob.getFullYear(),
        });
      }

      setIsLoading(false);
    }
  }, [loggedInUser]);

  useEffect(() => {
    if (editableUserValues) {
      validateDOB();
    }
  }, [dob]);

  const validateDOB = () => {
    if (dob.month && dob.day && dob.year) {
      const validAge = isCanada(countryCode)
        ? CANADA_AGE_REQUIREMENT
        : US_AGE_REQUIREMENT;
      const newDob = new Date(dob.year, dob.month - 1, dob.day);

      const isValidDob = differenceInYears(new Date(), newDob) >= validAge;

      setForm({ ...form, errorsOnSave: false, validDob: isValidDob });
      setEditableUserValues({
        ...editableUserValues,
        dateOfBirth: newDob.toISOString(),
      });
    } else if (!dob.month && !dob.day && !dob.year) {
      setForm({ ...form, validDob: true });
      setEditableUserValues({ ...editableUserValues, dateOfBirth: "" });
    } else if (!dob.month || !dob.day || !dob.year) {
      setForm({ ...form, validDob: false });
    }
  };

  const validateEmail = (e: FormEvent<HTMLInputElement>) => {
    const email = e.currentTarget.value;
    const emailIsEmpty = isEmpty(email);
    const validEmail = !emailIsEmpty && validate(email);
    const errorEmailText = emailIsEmpty
      ? "Email is required."
      : !validEmail
        ? "Please enter a valid email."
        : "";

    setForm({
      ...form,
      errorEmailText,
      validEmail,
    });

    setEditableUserValues({
      ...editableUserValues,
      email,
    });
  };

  const validatePhoneNumber = (e: FormEvent<HTMLInputElement>) => {
    const phoneNumber = e.currentTarget.value;
    let isValid = form.validPhoneNumber;

    if (isEmpty(phoneNumber)) {
      isValid = true;
    } else {
      isValid = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/.test(
        phoneNumber,
      );
    }

    setForm({
      ...form,
      validPhoneNumber: isValid,
    });

    setEditableUserValues({
      ...editableUserValues,
      phoneNumber,
    });
  };

  const updateUser = async (e: SyntheticEvent) => {
    e.preventDefault();

    setForm({ ...form, errorsOnSave: false, updateSuccess: false });

    if (form.validEmail && form.validDob) {
      try {
        if (editableUserValues) {
          const updatedUser = await putUser(editableUserValues, countryCode);

          if (updatedUser) {
            setForm({
              ...form,
              errorEmailText: "",
              errorsOnSave: false,
              updateSuccess: true,
            });

            dispatch<UserLoadAction>({
              type: USER_LOAD,
              userData: updatedUser,
            });
          }
        }
      } catch (e) {
        const {
          response: {
            data: {
              error: { email, username },
            },
          },
        } = e;

        if (email) {
          setForm({
            ...form,
            errorEmailText: `${(email as string[]).join("\n | ")}`,
          });
        }

        if (username) {
          setForm({
            ...form,
            errorUsernameText: `${(username as string[]).join("\n | ")}`,
          });
        }

        setForm({
          ...form,
          errorsOnSave: true,
          updateSuccess: false,
        });
      }
    } else {
      setForm({
        ...form,
        errorsOnSave: true,
        updateSuccess: false,
      });
    }
  };

  if (isLoading) {
    return (
      <UserProfileLayout currentPage="Edit profile">
        <div className="loading py-section">Loading...</div>
      </UserProfileLayout>
    );
  }

  return (
    <UserProfileLayout currentPage="Edit profile">
      <h2>Edit profile</h2>
      <form>
        <div className="row name-component-container">
          <div className="col md:col-10 lg:col-8">
            <Input
              id="name"
              className="fs-exclude my-sm"
              label="Legal Name"
              type="text"
              helperText="Your legal name is needed for checkout"
              activeOnLoad={!!editableUserValues?.name}
              value={editableUserValues?.name}
              onChange={(e: FormEvent<HTMLInputElement>) => {
                setEditableUserValues({
                  ...editableUserValues,
                  name: e.currentTarget.value,
                });
              }}
            />
            <Input
              id="preferred-name"
              className="fs-exclude my-sm"
              label="Preferred name"
              type="text"
              activeOnLoad={!!editableUserValues?.preferredName}
              helperText="What should we call you? We'll use this on-site and in other forms of communication between Leafly and you"
              value={editableUserValues?.preferredName}
              onChange={(e: FormEvent<HTMLInputElement>) => {
                setEditableUserValues({
                  ...editableUserValues,
                  preferredName: e.currentTarget.value,
                });
              }}
            />
            <Input
              id="username"
              className="fs-exclude my-sm"
              labelClasses="text-secondary"
              inputClasses="text-secondary"
              label="Username"
              type="text"
              value={editableUserValues?.username}
              helperText="You can use this to login, and stores will see this when you review"
              disabled
            />
            <EmailVerificationStatus />
            <Input
              id="user-email"
              label={
                <span>
                  Email<span className="text-error">*</span>
                </span>
              }
              type="email"
              className="fs-exclude my-sm"
              activeOnLoad={!!editableUserValues?.email}
              value={editableUserValues?.email}
              onChange={validateEmail}
              errorMessage={form.errorEmailText}
            />
            <Input
              id="Phone"
              className="fs-exclude my-sm"
              label="Mobile"
              type="tel"
              activeOnLoad={!!editableUserValues?.phoneNumber}
              value={editableUserValues?.phoneNumber}
              helperText="We won’t share this without your permission"
              onChange={validatePhoneNumber}
              errorMessage={
                !form.validPhoneNumber
                  ? "Please enter a valid mobile phone number (10 digits)."
                  : ""
              }
            />

            <DateSelectors
              countryCode={countryCode}
              handleSetDob={setDob}
              id="date-of-birth"
              label="Date of birth"
              savedDate={dob}
              yearsArrayType="ageLimit"
            />

            {!form.validDob ? (
              <div className="text-xs text-error pt-xs">
                Please enter a month, day and year.
              </div>
            ) : (
              <div className="form__field">
                <p>This helps us understand who’s using Leafly</p>
              </div>
            )}

            <div className="row justify-center">
              <button
                className="button col md:col-5 mx-xs mb-xxl mt-lg"
                tabIndex={0}
                onKeyPress={updateUser}
                onClick={updateUser}
              >
                save
              </button>
            </div>

            {form.updateSuccess && (
              <div className="bg-green-20 border-2 border-green text-center mb-xxl shadow-low rounded w-full p-md">
                Your profile was successfully updated.
              </div>
            )}

            {form.errorsOnSave && (
              <div className="bg-caryophyllene-20 border-2 border-error mb-xxl shadow-low rounded w-full p-md">
                Something went wrong, please try correcting the errors above.
              </div>
            )}

            <DeleteAccountButton />
          </div>
        </div>
      </form>
    </UserProfileLayout>
  );
};

export const getServerSideProps = createGetServerSideProps();

export default EditProfile;
